import { asRec, isRec, listaDe, pick, toFecha, toNum } from './adaptHelpers';
import { frecuenciaDias } from './freshness';
import { fuenteLabel } from './labels';
import { cambioPctExacto, restaExacta } from './numbers';
import { fuenteDetalleSchema, variacionSchema, type VariacionCruda } from './schemas';
import { clasificarTendencia } from './stats';
import type {
  FuenteDetalle,
  ISODateTime,
  ResumenMercado,
  Variacion,
  VariacionesResultado,
} from './types';

/** Adaptadores de la analítica del add-on: /v1/mercado/variaciones, /v1/mercado/resumen y /v1/fuentes/detalle. */

const PERIODO_POR_DEFECTO = 7;

const generado = (rec: Record<string, unknown>): ISODateTime | null => {
  const v = pick(rec, 'generado');
  return typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? v : null;
};

/** Completa Δ y tendencia cuando el servidor los omite pero hay valor previo. */
function derivados(c: VariacionCruda): Pick<Variacion, 'difAbs' | 'difPct' | 'tendencia'> {
  const difPct = c.dif_pct ?? (c.previo !== null ? cambioPctExacto(c.actual, c.previo, 2) : null);
  const difAbs = c.dif_abs ?? (c.previo !== null ? restaExacta(c.actual, c.previo, 4) : null);
  const tendencia =
    c.tendencia === 'sin_dato' && difPct !== null ? clasificarTendencia(difPct) : c.tendencia;
  return { difAbs, difPct, tendencia };
}

/** Una variación del servidor → dominio. null si la fila no sirve. */
export function adaptVariacion(raw: unknown, periodoDias = PERIODO_POR_DEFECTO): Variacion | null {
  const r = variacionSchema.safeParse(raw);
  if (!r.success) return null;
  const c = r.data;
  return {
    serie: { rubro: c.rubro, categoria: c.categoria, presentacion: c.presentacion },
    region: c.region,
    fuente: c.fuente,
    unidad: c.unidad ?? 'kg',
    actual: c.actual,
    fechaActual: c.fecha_actual,
    previo: c.previo,
    fechaPrevio: c.fecha_previo,
    ...derivados(c),
    periodoDias,
    puntos: c.puntos ?? c.spark.length,
    spark: c.spark,
  };
}

function variacionesDe(lista: unknown[], periodoDias: number): Variacion[] {
  const items = lista.map((v) => adaptVariacion(v, periodoDias));
  if (lista.length > 0 && items.every((v) => v === null))
    throw new Error('variaciones: ninguna fila es válida');
  return items.filter((v): v is Variacion => v !== null);
}

export function adaptVariaciones(raw: unknown): VariacionesResultado {
  const lista = listaDe(raw, ['variaciones', 'data', 'items', 'resultados']);
  if (!lista) throw new Error('variaciones: la respuesta no trae una lista');
  const rec = asRec(raw);
  const periodoDias =
    toNum(pick(rec, 'periodo_dias', 'periodoDias', 'periodo')) ?? PERIODO_POR_DEFECTO;
  return {
    periodoDias,
    generado: generado(rec),
    origen: 'servidor',
    variaciones: variacionesDe(lista, periodoDias),
  };
}

function moversDe(raw: unknown): Variacion[] {
  return Array.isArray(raw)
    ? raw
        .map((v) => adaptVariacion(v, PERIODO_POR_DEFECTO))
        .filter((v): v is Variacion => v !== null)
    : [];
}

export function adaptResumen(raw: unknown): ResumenMercado {
  if (!isRec(raw) || !isRec(raw.cobertura)) throw new Error('resumen: forma no reconocida');
  const cobertura = raw.cobertura;
  const movers = asRec(raw.movers);
  return {
    generado: generado(raw) ?? '',
    tasaBcvActual: toNum(raw.tasa_bcv_actual),
    tasaBcvFecha: toFecha(raw.tasa_bcv_fecha),
    tasaBcvVariacionPct7d: toNum(raw.tasa_bcv_variacion_pct_7d),
    cobertura: {
      series: toNum(cobertura.series) ?? 0,
      fuentes: toNum(cobertura.fuentes) ?? 0,
      regiones: toNum(cobertura.regiones) ?? 0,
      filas: toNum(cobertura.filas) ?? 0,
      ultimaActualizacion: toFecha(cobertura.ultima_actualizacion),
    },
    suben: moversDe(movers.suben),
    bajan: moversDe(movers.bajan),
  };
}

export function adaptFuentesDetalle(raw: unknown): FuenteDetalle[] {
  const lista = listaDe(raw, ['data', 'fuentes', 'items', 'resultados']);
  if (!lista) throw new Error('fuentes/detalle: la respuesta no trae una lista');
  const filas = lista.flatMap((item): FuenteDetalle[] => {
    const r = fuenteDetalleSchema.safeParse(item);
    if (!r.success) return [];
    const c = r.data;
    return [
      {
        clave: c.clave,
        nombre: c.nombre ?? fuenteLabel(c.clave),
        tipo: c.tipo,
        url: c.url,
        region: c.region,
        frecuencia: c.frecuencia,
        permiso: c.permiso,
        ultimoDato: c.ultimo_dato,
        frescura: c.frescura,
        diasDesdeUltimo: c.dias_desde_ultimo,
        frecuenciaDias: c.frecuencia_dias ?? frecuenciaDias(c.frecuencia),
        filas: c.filas ?? 0,
        rubros: c.rubros,
        regiones: c.regiones,
        activa: c.activa,
        notasPublicas: c.notas_publicas,
      },
    ];
  });
  if (lista.length > 0 && filas.length === 0)
    throw new Error('fuentes/detalle: ninguna fuente es válida');
  return filas;
}
